import { environment } from 'src/environments/environment';

type CarouselImageRecord = Record<string, unknown>;

function readTrimmedString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function readNestedImageUrl(value: unknown): string {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return '';
  }
  const record = value as CarouselImageRecord;
  const images = record['Images'];
  const firstImage =
    Array.isArray(images) && images.length > 0 && images[0] && typeof images[0] === 'object'
      ? (images[0] as CarouselImageRecord)
      : null;
  return (
    readTrimmedString(record['image_preview_url']) ||
    readTrimmedString(record['image_url']) ||
    readTrimmedString(record['url']) ||
    readTrimmedString(record['image']) ||
    readTrimmedString(firstImage?.['image_preview']) ||
    readTrimmedString(firstImage?.['image_original']) ||
    readTrimmedString(firstImage?.['image_url'])
  );
}

/** Resolve a carousel/banner slot value to a displayable image URL. */
export function resolveCarouselImageUrl(image: unknown): string {
  if (!image) {
    return '';
  }
  if (typeof image === 'string') {
    return image.trim();
  }
  if (typeof image !== 'object' || Array.isArray(image)) {
    return '';
  }
  const record = image as CarouselImageRecord;
  const previewUrl = readTrimmedString(record['previewUrl']);
  if (previewUrl) {
    return previewUrl;
  }
  const direct = readNestedImageUrl(record);
  if (direct) {
    return direct;
  }
  const cfImage = record['cf_image'];
  return readNestedImageUrl(cfImage);
}

function stripCssUrlWrapper(value: string): string {
  const trimmed = value.trim();
  if (!/^url\s*\(/i.test(trimmed)) {
    return trimmed;
  }
  return trimmed.replace(/^url\s*\(\s*["']?/i, '').replace(/["']?\s*\)$/i, '').trim();
}

/** Absolute URL suitable for `<img src>` / CSS backgrounds (prefixes API host for relative paths). */
export function normalizeMediaUrlForDisplay(
  url: string | null | undefined,
  apiHost: string = environment.apiUrlHost,
): string {
  let trimmed = stripCssUrlWrapper(url?.trim() ?? '');
  if (!trimmed || trimmed === 'none') {
    return '';
  }
  if (/^(https?:|data:|blob:)/i.test(trimmed)) {
    return trimmed;
  }
  const hostWithScheme = apiHost.replace(/\/$/, '');
  const hostBare = hostWithScheme.replace(/^https?:\/\//i, '');
  if (trimmed.startsWith(hostBare) || trimmed.startsWith(`${hostWithScheme}/`)) {
    return trimmed.startsWith('http') ? trimmed : `http://${trimmed.replace(/^\/+/, '')}`;
  }
  if (/^[a-z0-9.-]+(:\d+)?\//i.test(trimmed)) {
    return `http://${trimmed.replace(/^\/+/, '')}`;
  }
  if (trimmed.startsWith('/assets/') || trimmed.startsWith('assets/')) {
    return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  }
  if (trimmed.startsWith('/')) {
    return `${hostWithScheme}${trimmed}`;
  }
  return `${hostWithScheme}/${trimmed.replace(/^\//, '')}`;
}

/** Resolve editor/API image value to a normalized absolute display URL. */
export function resolveCarouselImageDisplayUrl(image: unknown): string {
  return normalizeMediaUrlForDisplay(resolveCarouselImageUrl(image));
}

/** Build a safe CSS `background-image` value for arbitrary image URLs. */
export function toCssBackgroundImageUrl(imageUrl: string | null | undefined): string {
  const normalized = normalizeMediaUrlForDisplay(imageUrl);
  if (!normalized) {
    return 'none';
  }
  const escaped = normalized.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return `url("${escaped}")`;
}
